// src/ui/popover.ts (T1): places a small fixed-position layer (info tip, menu) next to its anchor, inside the
// viewport, so it never causes horizontal scroll on a 360px phone. Below the anchor by default; above when
// there is no room below.
import { useLayoutEffect } from "preact/hooks";
import type { RefObject } from "preact";

const MARGIN = 8;   // from the viewport edge
const GAP = 6;      // from the anchor

export function placePopover(anchor: HTMLElement, pop: HTMLElement, align: "start" | "center" | "end" = "start"): void {
  // Where `left: 0; top: 0` puts the layer: the viewport's corner, or the corner of an ancestor the browser makes its
  // containing block (a transform or a filter does). Measured, not guessed from styles: a size container (the family
  // page, `.pd`) is not one in WebKit or Chromium, and guessing that it was sent every menu on the page far from its
  // button.
  pop.style.left = "0px";
  pop.style.top = "0px";
  const p = pop.getBoundingClientRect();
  const o = { x: p.left, y: p.top };
  const a = anchor.getBoundingClientRect();
  const vw = document.documentElement.clientWidth || window.innerWidth;
  const vh = window.innerHeight;
  let left = align === "center" ? a.left + a.width / 2 - p.width / 2 : align === "end" ? a.right - p.width : a.left;
  left = Math.min(Math.max(MARGIN, left), vw - p.width - MARGIN);
  const below = a.bottom + GAP;
  const above = a.top - GAP - p.height;
  const top = below + p.height <= vh - MARGIN || above < MARGIN ? below : above;
  pop.style.left = `${Math.round(left - o.x)}px`;
  pop.style.top = `${Math.round(top - o.y)}px`;
  pop.dataset.side = top === below ? "bottom" : "top";
}

/** Keeps `pop` placed against `anchor` while `open`, following scroll and resize. */
export function usePopoverPlacement(
  open: boolean,
  anchor: RefObject<HTMLElement>,
  pop: RefObject<HTMLElement>,
  align: "start" | "center" | "end" = "start",
): void {
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      if (anchor.current && pop.current) placePopover(anchor.current, pop.current, align);
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, align]);
}

/** Calls `onOutside` for a pointer-down outside every given element, and for Escape. */
export function useDismiss(open: boolean, refs: RefObject<HTMLElement>[], onOutside: () => void): void {
  useLayoutEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (refs.some((r) => r.current && t && r.current.contains(t))) return;
      onOutside();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onOutside();
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);
}
